import axiosInstance from "./axiosInstance";

// In-memory cache — เก็บ profile ครั้งเดียว ไม่ต้องยิง API ซ้ำทุกครั้ง component mount
let _profileCache = null;
let _profileFetchPromise = null;

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    return null;
  }
}
let refreshTimer = null;

export function startTokenMonitor() {
  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!token || !refreshToken) {
    console.warn("No token or refreshToken found");
    return;
  }

  const payload = parseJwt(token);
  if (!payload || !payload.exp) {
    console.warn("Invalid token payload");
    return;
  }

  const currentTime = Math.floor(Date.now() / 1000); // ปัจจุบัน (วินาที)
  const expireTime = payload.exp; // เวลาหมดอายุ
  const timeLeft = expireTime - currentTime;

  // ล็อกเวลาดู
  // console.log(" Token created at:", new Date(payload.iat * 1000).toLocaleTimeString());
  // console.log(" Token expires at:", new Date(payload.exp * 1000).toLocaleTimeString());
  // console.log(" Seconds left before expiration:", timeLeft);

  // ตั้งเวลาให้เรียก refresh ก่อนหมดอายุ 5 นาที (300 วินาที)
  const refreshBefore = 300;
  const refreshIn = Math.max(0, (timeLeft - refreshBefore) * 1000);

  if (refreshTimer) clearTimeout(refreshTimer);

  refreshTimer = setTimeout(async () => {
    // console.log(" Refreshing token now...");
    try {
      const token = localStorage.getItem("token"); // token เก่าที่ใช้แทน refresh token

      if (!token) {
        console.warn("No token found in localStorage");
        return;
      }

      const res = await axiosInstance.post("/refresh-token", {
        refreshToken: token,
      });

      const newToken = res.data.token;
      localStorage.setItem("token", newToken);
      // console.log(" Token refreshed:", new Date().toLocaleTimeString());

      // เรียกตัวเองใหม่เพื่อตั้ง timer ใหม่
      startTokenMonitor();
    } catch (error) {
      console.error("Refresh token failed:", error);
      // Redirect to login หรือแจ้งเตือน
    }
  }, refreshIn);
}

const authService = {
  // Login
  login: async (email, password) => {
    try {
      const response = await axiosInstance.post(`/login`, { email, password });
      const { token } = response.data;

      localStorage.setItem("token", token);
      startTokenMonitor(); // เริ่มจับเวลา

      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  getUserProfile: async () => {
    // ถ้ามี cache อยู่แล้ว คืนค่าทันทีโดยไม่ยิง API
    if (_profileCache) return _profileCache;

    // ถ้ามีการยิง API อยู่แล้ว รอผลเดิมแทนการยิงซ้ำ (deduplication)
    if (_profileFetchPromise) return _profileFetchPromise;

    _profileFetchPromise = (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");
        const response = await axiosInstance.get(`/getUserProfile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        _profileCache = response.data;
        return response.data;
      } catch (error) {
        _profileFetchPromise = null; // ล้าง promise เผื่อ retry
        throw error.response ? error.response.data : error;
      } finally {
        _profileFetchPromise = null;
      }
    })();

    return _profileFetchPromise;
  },

  updateProfile: async (updateData) => {
    try {
      const token = localStorage.getItem("token"); // ดึง token
      if (!token) throw new Error("No token found");

      // ยิง PATCH ไปที่ /api/user/profile
      const response = await axiosInstance.patch(`/user/profile`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      _profileCache = null; // ✅ Clear cache to force fresh fetch on next call
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // ฟังก์ชันอัปโหลดรูปโปรไฟล์
  uploadProfileImage: async (file) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      // เอาไฟล์รูปยัดใส่ FormData
      const formData = new FormData();
      // หมายเหตุ: ตรง "profile_image" คือชื่อฟิลด์ที่ฝั่ง API รอรับ (เปลี่ยนให้ตรงกับ Backend)
      formData.append("profile_image", file); 

      const response = await axiosInstance.patch(`/user/profile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data", // บังคับใส่ บอก Backend ว่าก้อนนี้คือไฟล์รูป
        },
      });
      _profileCache = null; // ✅ Clear cache to force fresh fetch on next call
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  logout: () => {
    // ล้าง cache ด้วยทุกครั้งที่ logout
    _profileCache = null;
    _profileFetchPromise = null;
    localStorage.removeItem("token");
    localStorage.removeItem("dbId");
  },

  // Register
  register: async (userData) => {
    try {
      const response = await axiosInstance.post(`/register`, userData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Register Technician
  sendRegisterTechnician: async (userData) => {
    try {
      const response = await axiosInstance.post(
        `/auth/technicians/register`,
        userData,
      );
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Forgot Password
  forgotPassword: async (email) => {
    try {
      const frontendBaseUrl = window.location.origin;
      const response = await axiosInstance.post(`/forgot-password`, {
        email,
        frontendBaseUrl,
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  // Reset Password
  resetPassword: async (token, newPassword) => {
    try {
      const response = await axiosInstance.post(`/reset-password`, {
        token,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  getAutodeskToken: async (req, res) => {
    try {
      const { email } = req.body;

      // ตรวจสอบ token/สิทธิ์ของ user จาก email (ถ้าจำเป็น)

      const token = await getToken(); // ฟังก์ชันนี้คุณมีแล้ว
      res.json({ token });
    } catch (err) {
      res.status(500).json({ error: "Failed to get Autodesk token" });
    }
  },

  // Check Email Availability
  checkEmail: async (email) => {
    try {
      const response = await axiosInstance.post(`/check-email`, { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  registerTechnician: async (technicianData) => {
    try {
      const emailCheck = await authService.checkEmail(technicianData.email);
      const isAvailable =
        emailCheck?.available === true ||
        emailCheck?.exists === false ||
        emailCheck?.message === "Email is available";

      if (!isAvailable) {
        throw {
          message: emailCheck?.message || "อีเมลนี้ถูกใช้งานแล้ว",
        };
      }

      // ส่ง technicianData ตรงๆ ไม่ต้องสร้าง payload ใหม่
      const response = await authService.sendRegisterTechnician(technicianData);
      console.log("Register Technician response:", response);
      return response;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  getTechniciansByCompany: async (company_name) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) throw new Error("No token found");

      const response = await axiosInstance.post(
        `/get-Technician`,
        { company_name },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  getTechnicianData: async (techId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.get(
        `/repair-general/technician/${techId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // คืนค่าข้อมูลที่อยู่ใน data (อิงตามโครงสร้าง { success: true, data: [] })
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },

  getTechnicianDataIoT: async (techId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axiosInstance.get(
        `/repair-iot/technician/${techId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // คืนค่าข้อมูลที่อยู่ใน data (อิงตามโครงสร้าง { success: true, data: [] })
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : error;
    }
  },
};

export default authService;